import React from 'react';

/**
 * P0-4: error boundary around the redress tab mount points.
 *
 * A data-shape surprise inside the redress screens previously threw during
 * render, and with no boundary anywhere in the app React unmounted the
 * ENTIRE root — a single malformed case white-screened the admin portal.
 * This boundary contains the failure to the redress panel, shows the error
 * inline, and offers a "Try again" reset that re-renders the children.
 */

interface RedressErrorBoundaryProps {
  children: React.ReactNode;
}

interface RedressErrorBoundaryState {
  error: Error | null;
}

export default class RedressErrorBoundary extends React.Component<
  RedressErrorBoundaryProps,
  RedressErrorBoundaryState
> {
  state: RedressErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): RedressErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error(
      'Redress tab crashed:',
      error,
      errorInfo?.componentStack ?? '(no component stack)',
    );
  }

  handleReset = (): void => {
    this.setState({ error: null });
  };

  render(): React.ReactNode {
    const { error } = this.state;
    if (error !== null) {
      return (
        <div
          role="alert"
          style={{
            margin: 12,
            padding: 16,
            borderRadius: 8,
            border: '1px solid #fecaca',
            background: '#fef2f2',
            color: '#991b1b',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            Something went wrong in the redress tab.
          </div>
          <div
            style={{
              fontSize: 13,
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              marginBottom: 12,
            }}
          >
            {error.message || String(error)}
          </div>
          <button
            type="button"
            onClick={this.handleReset}
            style={{
              padding: '6px 14px',
              borderRadius: 6,
              border: '1px solid #991b1b',
              background: '#ffffff',
              color: '#991b1b',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
